import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Check, Download, Mail, ShieldCheck, ExternalLink } from "lucide-react";
import { track } from "@/lib/analytics";
import AiIntakeStep from "@/pages/tailor/AiIntakeStep";
import BriefReviewStep from "@/pages/tailor/BriefReviewStep";
import { emptyBrief, durationLabel } from "@/pages/tailor/briefUtils";

const IADA_LABEL = {
  yes: "Con aval IADA (+30%)",
  no: "Sin aval IADA (solo DCC)",
  both: "Ambas opciones (comparativo)",
};

export default function TailorMade() {
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");
  const [step, setStep] = useState(1);
  const [intake, setIntake] = useState(null);
  const [brief, setBrief] = useState(null);
  const [wantsIada, setWantsIada] = useState("both");
  const [aiSuggestionId, setAiSuggestionId] = useState(null);
  const [initialSuggestedIds, setInitialSuggestedIds] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", company: "", phone: "", role: "", comments: "", consent: true });
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resumeLoaded, setResumeLoaded] = useState(false);

  useEffect(() => {
    api.get("/catalog/programs?limit=200").then((r) => setPrograms(r.data.items));
  }, []);

  // Bloque 2 · rehydrate from ?resume=<id> (QR resume flow) — idempotent under StrictMode
  useEffect(() => {
    if (!resumeId || resumeLoaded) return;
    setResumeLoaded(true); // set BEFORE the request so the second effect run is a no-op
    api.get(`/public/tailor-made/${resumeId}`)
      .then((r) => {
        const d = r.data || {};
        if (d.brief) setBrief(d.brief);
        if (d.wants_iada) setWantsIada(d.wants_iada);
        if (d.contact) setContact((c) => ({ ...c, ...(d.contact || {}) }));
        setResult({ request_id: d.id, pdf_url: d.pdf_url, total_hours_estimated: d.total_hours_estimated });
        setStep(d.brief ? 2 : 1);
        toast.success("Propuesta retomada desde QR. Revisa y descarga o envía a un asesor.");
      })
      .catch(() => { toast.error("No pudimos retomar la propuesta desde el enlace."); });
  }, [resumeId, resumeLoaded]);

  const onGenerated = (intakeForm, aiData) => {
    setIntake(intakeForm);
    if (intakeForm.wants_iada) setWantsIada(intakeForm.wants_iada);
    setBrief({
      kyc: aiData.kyc, structure: aiData.structure, logistics: aiData.logistics,
      content: aiData.content, operations: aiData.operations,
      overall_reasoning: aiData.overall_reasoning,
    });
    setAiSuggestionId(aiData.ai_suggestion_id);
    setInitialSuggestedIds((aiData.content?.suggested_program_ids || []).map((m) => m.program_id));
    if (intakeForm.company) setContact((c) => ({ ...c, company: intakeForm.company }));
    setStep(2);
  };

  const onSkip = () => {
    setIntake({ challenge: "", audience_size: 20, wants_iada: "both" });
    setBrief(emptyBrief());
    setStep(2);
  };

  const onRegenerate = async () => {
    if (!intake?.challenge) { toast.error("Vuelve al paso 1 para modificar el reto."); return; }
    try {
      const { wants_iada, ...aiPayload } = intake;
      const { data } = await api.post("/public/tailor-made/ai-suggest", aiPayload, { timeout: 45000 });
      onGenerated(intake, data);
      toast.success("Propuesta regenerada.");
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail));
    }
  };

  const buildPayloadCore = () => {
    const selectedIds = (brief?.content?.suggested_program_ids || []).map((m) => m.program_id);
    const totalH = (brief?.logistics?.attendees_per_group || 20) * (brief?.logistics?.session_duration_hours || 1);
    return {
      program_name: brief?.content?.training_name || "Programa Tailor-Made",
      target_audience: brief?.content?.graduate_profile || "",
      desired_duration: durationLabel(totalH),
      modality: brief?.structure?.modality || "blended",
      selected_program_ids: selectedIds,
      ai_suggestion_id: aiSuggestionId,
      brief,
      wants_iada: wantsIada,
      selectedIds,
    };
  };

  // Bloque 2 · direct PDF download without lead capture
  const downloadPdfDirect = async () => {
    const core = buildPayloadCore();
    if (core.selectedIds.length < 3) { toast.error("Se requieren mínimo 3 módulos."); return; }
    setDownloading(true);
    try {
      const payload = {
        program_name: core.program_name,
        target_audience: core.target_audience,
        desired_duration: core.desired_duration,
        modality: core.modality,
        selected_program_ids: core.selected_program_ids,
        ai_suggestion_id: core.ai_suggestion_id,
        brief: core.brief,
        wants_iada: core.wants_iada,
        // contact intentionally omitted — direct download flow
      };
      const { data } = await api.post("/public/tailor-made/generate-pdf", payload);
      track("tailor_made_pdf_downloaded", { has_ai: !!aiSuggestionId, wants_iada: wantsIada });
      setResult(data);
      const pdfUrl = data.pdf_url?.startsWith("/api") ? `${BACKEND_URL}${data.pdf_url}` : data.pdf_url;
      if (pdfUrl) window.open(pdfUrl, "_blank", "noopener");
      toast.success("PDF generado · se abrió en una nueva pestaña.");
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail));
    } finally { setDownloading(false); }
  };

  const submitWithContact = async () => {
    if (!contact.consent) { toast.error("Debes aceptar el consentimiento."); return; }
    if (!contact.name || !contact.email || !contact.company) {
      toast.error("Nombre, correo y empresa son requeridos para enviar a un asesor.");
      return;
    }
    setSubmitting(true);
    try {
      const core = buildPayloadCore();
      if (core.selectedIds.length < 3) { toast.error("Se requieren mínimo 3 módulos."); setSubmitting(false); return; }
      const payload = {
        program_name: core.program_name,
        target_audience: core.target_audience,
        desired_duration: core.desired_duration,
        modality: core.modality,
        selected_program_ids: core.selected_program_ids,
        contact,
        ai_suggestion_id: core.ai_suggestion_id,
        brief: core.brief,
        wants_iada: core.wants_iada,
      };
      const { data } = await api.post("/public/tailor-made", payload);
      const kept = core.selected_program_ids.filter((id) => initialSuggestedIds.includes(id)).length;
      const removed = initialSuggestedIds.length - kept;
      track("ai_suggest_accepted", { modules_kept_from_ai: kept, modules_removed_from_ai: removed });
      track("tailor_made_submitted", { has_ai: !!aiSuggestionId, wants_iada: wantsIada });
      setResult(data);
      setStep(4);
      toast.success("Solicitud recibida.");
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Tailor-Made</p>
        <h1 className="mt-2 text-4xl font-bold text-[#0B0B0B] font-display">Diseña tu programa a la medida</h1>
        <p className="mt-2 text-gray-600 max-w-2xl">
          Un asistente con IA te ayuda a componer un brief completo en 4 pasos. Descarga el PDF directo
          o envía la propuesta a un asesor DCC.
        </p>

        <Stepper step={step} />

        <div className="mt-8" data-testid="tailor-wizard-step">
          {step === 1 && <AiIntakeStep onGenerated={onGenerated} onSkip={onSkip} />}
          {step === 2 && brief && (
            <BriefReviewStep
              brief={brief}
              setBrief={setBrief}
              programs={programs}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              onRegenerate={onRegenerate}
              regenerateEnabled={!!aiSuggestionId}
            />
          )}
          {step === 3 && (
            <DownloadStep
              wantsIada={wantsIada}
              setWantsIada={setWantsIada}
              contact={contact}
              setContact={setContact}
              onBack={() => setStep(2)}
              downloadPdfDirect={downloadPdfDirect}
              submitWithContact={submitWithContact}
              downloading={downloading}
              submitting={submitting}
              lastResult={result}
            />
          )}
          {step === 4 && result && <SuccessStep result={result} />}
        </div>
      </section>
    </PublicLayout>
  );
}

function Stepper({ step }) {
  const steps = [
    { n: 1, l: "Cuéntanos" },
    { n: 2, l: "Revisa brief" },
    { n: 3, l: "Descarga o envía" },
    { n: 4, l: "Confirmación" },
  ];
  return (
    <div className="mt-8 flex items-center gap-2 flex-wrap text-xs uppercase tracking-widest" data-testid="tailor-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`flex items-center gap-2 ${step === s.n ? "text-[#F26A21]" : step > s.n ? "text-emerald-600" : "text-gray-400"}`}>
            <span className={`w-6 h-6 flex items-center justify-center rounded-full border font-mono ${
              step > s.n ? "bg-emerald-500/10 border-emerald-500/40" :
              step === s.n ? "bg-[#F26A21]/10 border-[#F26A21]/40" : "border-gray-300"
            }`}>{step > s.n ? <Check className="w-3 h-3" /> : s.n}</span>
            <span className="font-semibold">{s.l}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 min-w-4 ${step > s.n ? "bg-emerald-500/40" : "bg-gray-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function DownloadStep({
  wantsIada, setWantsIada, contact, setContact,
  onBack, downloadPdfDirect, submitWithContact,
  downloading, submitting, lastResult,
}) {
  const pdfUrl = lastResult?.pdf_url?.startsWith("/api") ? `${BACKEND_URL}${lastResult.pdf_url}` : lastResult?.pdf_url;
  return (
    <div className="space-y-6" data-testid="download-step">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Paso 3</p>
        <h2 className="text-2xl font-bold text-[#0B0B0B] font-display mt-1">
          Descarga la propuesta o envíala a un asesor
        </h2>
        <p className="mt-2 text-sm text-gray-600 max-w-2xl">
          Puedes descargar el PDF profesional ahora mismo (sin dejar datos de contacto) o
          enviar la propuesta a un asesor DCC para recibir cotización cerrada, calendario
          y soporte para tu equipo.
        </p>
      </div>

      {/* IADA choice reminder */}
      <div
        className="p-4 rounded-lg bg-[#F26A21]/5 border border-[#F26A21]/40 flex flex-wrap items-center gap-3"
        data-testid="iada-choice-summary"
      >
        <ShieldCheck className="w-5 h-5 text-[#F26A21] flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-[#F26A21] font-bold">Aval académico</p>
          <p className="text-sm font-semibold text-[#0B0B0B]">{IADA_LABEL[wantsIada] || IADA_LABEL.both}</p>
        </div>
        <select
          value={wantsIada}
          onChange={(e) => setWantsIada(e.target.value)}
          data-testid="iada-choice-select"
          className="text-xs bg-white border border-gray-300 rounded px-2 py-1.5 text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
        >
          <option value="yes">Con aval IADA (+30%)</option>
          <option value="no">Sin aval IADA</option>
          <option value="both">Ambas opciones</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Card A: Direct download */}
        <div className="p-6 rounded-2xl border-2 border-[#F26A21] bg-white" data-testid="download-card-direct">
          <p className="text-[11px] uppercase tracking-widest text-[#F26A21] font-bold">Opción rápida</p>
          <h3 className="mt-2 text-lg font-bold text-[#0B0B0B]">Descarga el PDF ahora</h3>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            Recibe el documento profesional al instante. Multi-página A4, con inversión
            estimada, módulos seleccionados y QR para retomar más tarde.
          </p>
          <ul className="mt-3 space-y-1 text-xs text-gray-700">
            <li>· Sin formulario · Sin correo requerido</li>
            <li>· Compártelo con tu equipo</li>
            <li>· Escanea el QR y retoma la propuesta</li>
          </ul>
          <button
            type="button"
            onClick={downloadPdfDirect}
            disabled={downloading}
            data-testid="download-pdf-direct-btn"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm disabled:opacity-60 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generando PDF..." : "Descargar propuesta PDF"}
          </button>
          {pdfUrl && (
            <div className="mt-3">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="download-pdf-open-again"
                className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:text-[#D9541A] underline underline-offset-2"
              >
                Volver a abrir el PDF <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>

        {/* Card B: Send to advisor */}
        <div className="p-6 rounded-2xl border border-gray-300 bg-[#F9FAFB]" data-testid="download-card-advisor">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Opción con acompañamiento</p>
          <h3 className="mt-2 text-lg font-bold text-[#0B0B0B]">Envía a un asesor DCC</h3>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            Un consultor DCC senior te contactará en las próximas 24 h con cotización cerrada
            y calendario personalizado.
          </p>

          <div className="mt-4 grid gap-2">
            <F label="Nombre *" val={contact.name} on={(v) => setContact({ ...contact, name: v })} testid="ct-name" />
            <F label="Correo *" type="email" val={contact.email} on={(v) => setContact({ ...contact, email: v })} testid="ct-email" />
            <F label="Empresa *" val={contact.company} on={(v) => setContact({ ...contact, company: v })} testid="ct-company" />
            <F label="Teléfono" val={contact.phone} on={(v) => setContact({ ...contact, phone: v })} testid="ct-phone" />
          </div>

          <label className="mt-3 flex items-start gap-2 text-[11px] text-[#0B0B0B]">
            <input
              type="checkbox"
              checked={contact.consent}
              onChange={(e) => setContact({ ...contact, consent: e.target.checked })}
              data-testid="ct-consent"
              className="mt-0.5 accent-[#F26A21]"
            />
            Acepto que DCC me contacte y comparta el brief con IADA · Anáhuac Business School si aplica el aval.
          </label>

          <button
            type="button"
            onClick={submitWithContact}
            disabled={submitting}
            data-testid="ct-submit"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0B0B0B] hover:bg-black text-white font-semibold text-sm disabled:opacity-60 transition-all"
          >
            <Mail className="w-4 h-4" />
            {submitting ? "Enviando..." : "Enviar a asesor DCC"}
          </button>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} data-testid="download-back-btn" className="text-sm text-gray-600 hover:text-[#0B0B0B]">
          ← Regresar al brief
        </button>
      </div>
    </div>
  );
}

function SuccessStep({ result }) {
  const pdfUrl = result.pdf_url?.startsWith("/api") ? `${BACKEND_URL}${result.pdf_url}` : result.pdf_url;
  return (
    <div className="max-w-2xl p-8 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl" data-testid="tailor-success">
      <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Solicitud recibida</p>
      <h2 className="mt-2 text-2xl font-bold text-[#0B0B0B] font-display">¡Gracias! Brief guardado.</h2>
      <p className="mt-2 text-sm text-gray-700">Un asesor DCC te contactará en las próximas 24 h.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B0B0B] hover:bg-black text-white text-sm font-semibold">
            <Download className="w-4 h-4" /> Descargar brief PDF
          </a>
        )}
        <a href="mailto:contacto@dcc.mx" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 text-[#0B0B0B] text-sm font-semibold hover:bg-black/5">
          <Mail className="w-4 h-4" /> Escríbenos
        </a>
      </div>
    </div>
  );
}

function F({ label, val, on, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} type={type}
        data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}
